// Texto de /consultoria. Fuente de verdad: knowledgeforge/consulting/brochure-es.md (brochure v8).
// Solo español de México, sin precios, sin em-dash ni flechas, y sin nombres ni logos de clientes
// cerca de los casos: un lector no debe poder deducir qué caso es de quién.

export const CALENDLY_URL = 'https://calendly.com/aaron-jags/mi-agenda';

export const CONSULTING = {
  meta: {
    title: 'Software con IA · Aarón García',
    description:
      'Haz tu producto con IA sin perder el control: nada llega a tus clientes roto y sin que sepas por qué. Monto todo lo de alrededor de tu IA: que cada cambio se pida bien, se revise, se pruebe y salga a tus clientes.',
  },
  hero: {
    kicker: 'Software con IA',
    title: 'Haz tu producto con IA sin perder el control: nada llega a tus clientes roto y sin que sepas por qué.',
    subtitle:
      'Ya tienes la idea y pagas una suscripción de IA. Lo que no viene con la suscripción es todo lo de alrededor: que cada cambio se pida bien, alguien lo revise, se pruebe y salga a tus clientes sin romper nada ni exponer tus contraseñas. Eso es lo que monto, contigo o dentro de tu equipo, y queda funcionando para que tu IA comience a construir sin tropiezos.',
    cta: 'Agenda tu demo de 30 minutos',
  },
  pillars: [
    {
      title: 'Controlada',
      desc: 'La IA solo cambia lo que tú autorizaste. Nada llega a tus clientes sin que alguien de tu lado lo revise y lo apruebe.',
    },
    {
      title: 'Precisa',
      desc: 'Antes de que la IA haga nada, queda por escrito qué tiene que cumplir. Así no rellena los huecos con suposiciones.',
    },
    {
      title: 'Medida',
      desc: 'Cuánto tarda cada entrega, cuánto se rehace y cuánto gastas en IA, antes y después. Con números, no por corazonada.',
    },
  ],
  audience: {
    title: 'Para quién',
    desc: 'Para quien paga una suscripción de IA y quiere construir o mejorar un producto sin pagar un equipo de desarrollo. Y para equipos de 2 a 30 personas que ya programan con Copilot, Cursor o Claude Code.',
    question: '¿Lo que te da la IA ya se puede cobrar, o solo se ve bien en pantalla?',
  },
  problem: {
    title: 'El problema que resuelvo',
    intro:
      'Con una suscripción de IA hoy cualquiera consigue algo que se ve bien en una pantalla. Lo que la suscripción no trae es lo de alrededor: quién revisa, qué puede tocar la IA, dónde quedan tus contraseñas y cómo sabes que de verdad funciona con clientes reales.',
    items: [
      'Le pides algo a la IA de forma vaga y te da algo que se ve bien. Lo que no pediste bien, lo inventa, y lo descubres cuando ya está frente a un cliente.',
      'Nadie revisa lo que la IA escribió en segundos con el cuidado de algo que costó días. Se aprueba porque funcionó en la demo.',
      'Nadie decidió qué puede tocar la IA: publicar cambios, entrar a servicios de terceros, usar tus contraseñas, gastar de tus cuentas.',
      'No sabes si vas más rápido o solo más ocupado, y la cuenta de la IA llega junta.',
    ],
    closing: 'La velocidad que no se mide se siente igual que la que sí existe.',
  },
  services: {
    title: 'Los tres servicios',
    items: [
      {
        name: 'Diagnóstico',
        when: '1 a 2 semanas · alcance cerrado',
        intro:
          'En dos semanas te digo, con números, qué tan lejos está lo que tienes de poder venderse. Te llevas:',
        list: [
          'Cuánto tarda hoy sacar un cambio y dónde se atora.',
          'Un mapa de lo que tienes, como está de verdad, y qué está expuesto, por gravedad.',
          'En qué se está yendo tu dinero de IA.',
          'Los tres arreglos que más rinden. Los sencillos te los entrego ya hechos.',
        ],
        closing: 'Te sirve aunque no sigas con nada más.',
      },
      {
        name: 'Puesta en marcha',
        when: '4 a 8 semanas · paras donde quieras',
        intro: 'Armo todo lo de alrededor de tu IA, en cinco pasos:',
        list: [
          'Candados: qué puede tocar la IA y qué no.',
          'Medición: cuánto tarda hoy, para comparar después.',
          'Encargos: cada pedido dice por escrito qué debe cumplir.',
          'Revisión: la IA no aprueba su propio trabajo.',
          'Entrega: el cambio llega a tu cliente, no se queda aprobado en un cajón.',
        ],
        ordered: true,
        closing:
          'Acelerar sin candados ni medición solo acelera los errores. Lo que ya funciona no se toca: se envuelve.',
      },
      {
        name: 'Operación y acompañamiento',
        when: 'mensual',
        intro:
          'Me quedo cuidando que siga funcionando: entreno a tu gente, vigilo velocidad y gasto, y si algo se rompe respondo en el tiempo que acordemos por contrato. Los respaldos se prueban restaurándolos.',
        list: [] as string[],
        closing: '',
      },
    ],
    guarantee: {
      title: 'Garantía',
      desc: 'Cada fase termina funcionando en tu día a día, y la puesta en marcha cierra con la medición de antes y después. Si no quedó funcionando, la fase no cerró. No entrego PDFs.',
    },
  },
  proof: {
    title: 'Recibos, no promesas',
    intro:
      'Validé este flujo de trabajo en mis últimos tres proyectos: una plataforma comunitaria, un marketplace y una app de movilidad con pagos. Casos reales que podemos ver en llamada.',
    cases: [
      {
        label: 'Plataforma comunitaria · equipo de 4',
        title: 'Todo el proceso desde la semana 1.',
        body: 'Ningún cambio se saltó la revisión desde el primer día. Nadie aprueba su propio trabajo: ni la IA ni quien la usó. Quien entra nuevo arranca leyendo la guía, sin sesiones para explicarle.',
        lesson: 'Ordenar en la semana 1 cuesta una décima parte que ordenar después.',
      },
      {
        label: 'Marketplace · app heredada, en vivo',
        title: 'El revisor, medido contra la persona.',
        body: 'Candados sobre la app vieja sin tocar el núcleo. Al comparar la revisión automática con lo que aprobaron personas, coincidió casi siempre; la vez que no, tenía razón la máquina: se aprobó un cambio sin la prueba que pedía el encargo.',
        lesson: 'Ni la persona ni la máquina son el árbitro: se mide a las dos.',
      },
      {
        label: 'Movilidad y pagos · sistema en vivo',
        title: 'Medir antes de arreglar.',
        body: 'Dos fallas de pagos corregidas en producción el mismo día, midiendo primero. Una ruta pasó de colgarse 30 segundos a responder en 1.5.',
        lesson: 'Se arregla lo que se midió, no lo que se sospecha.',
      },
    ],
  },
  rule: {
    title: 'Mi regla',
    headline: 'Medir antes de confiar.',
    desc: 'Ningún cambio de la IA entra porque se ve bien: pasa pruebas automáticas y alguien de tu lado lo aprueba.',
    about:
      'Soy ingeniero de software, con más de 10 años construyendo producto sobre Flutter, React Native, Node/TypeScript y Python. Lo que mejor hago es llegar a proyectos donde no hay control y la fecha ya está encima: primero mido dónde se atora la entrega, luego ordeno la arquitectura, vuelvo confiable el release y saco la entrega sin romper lo que ya funciona. Opero todos los días mi propio proceso con IA: agentes de IA que proponen cambios, herramientas propias que los revisan y alguien del equipo que aprueba al final.',
  },
  notThis: {
    title: 'Lo que no es',
    items: [
      {
        title: 'No es un servicio que programa por ti.',
        desc: 'Se monta contigo o dentro de tu equipo, es tuya y sigue funcionando si dejamos de vernos.',
      },
      {
        title: 'No sustituye a tu equipo ni te amarra a una marca de IA.',
        desc: 'Sirve con la herramienta que ya usen.',
      },
      {
        title: 'No te saca de tu nube.',
        desc: 'Si lo prefieres, todo corre en tu cuenta de AWS, con tus llaves y tus registros.',
      },
      {
        title: 'No promete "el doble de rápido".',
        desc: 'Primero se mide cuánto tarda hoy una entrega, y la mejora se reporta con ese mismo número.',
      },
      {
        title: 'No promete bajar tu gasto en IA sin medirlo.',
        desc: 'A veces baja mucho y a veces lo barato sale caro.',
      },
    ],
  },
  contact: {
    title: 'Tráeme tu idea a un demo de 30 minutos',
    desc: 'Me cuentas qué quieres lanzar y con qué lo estás haciendo, y te muestro en vivo cómo se ve cuando la IA trabaja con candados, revisión y medición. Sin propuesta antes de medir.',
    cta: 'Agenda tu demo de 30 minutos',
    emailLabel: 'O escríbeme',
  },
};
